/**
 * What the three sign-in landing pages show while they hand the user on — the
 * emailed login link, the signup verification link, and the LinkedIn callback.
 *
 * A CENTRED SPINNER. THAT IS ALL.
 * -------------------------------
 * These pages used to be confirmation screens: a card, a tick, a headline
 * saying the sign-in had worked, and a countdown of 3, 5 and 2.2 seconds
 * respectively. A user closed the window during one of them, which on the
 * signup flow is indistinguishable from abandoning the signup.
 *
 * The countdown was the acute problem, but the card was the underlying one. A
 * bordered panel with a heading is the visual language of a screen that wants
 * something from you, and there is nothing to want: the page is a redirect with
 * a network call in the middle. Telling someone their login succeeded is also
 * telling them what they already know — they clicked a link to sign in, and the
 * dashboard appearing says it better than a tick does.
 *
 * So what is left is the honest minimum: a spinner, because something really is
 * in flight, and nothing that could be mistaken for a destination.
 *
 * THESE PAGES MUST BE ROUTED OUTSIDE `AuthLayout`.
 * -----------------------------------------------
 * `AuthLayout` wraps its outlet in a `max-w-[54.5rem]` bordered card with a
 * logo and a footer. Put this component inside it and the centred spinner
 * becomes a spinner in a panel — the exact card chrome the paragraph above says
 * to avoid, reintroduced by the route table rather than by this file. All three
 * routes sit at the top level of `App.tsx` for that reason.
 *
 * THERE IS NO TIMER ANYWHERE IN THIS FLOW.
 * ----------------------------------------
 * The spinner lasts exactly as long as the token exchange, which is a second or
 * two. Nothing waits on a `setTimeout` before navigating, and nothing should be
 * added that does: a fixed hold is the original complaint, whether or not a
 * number is counting down next to it.
 *
 * THE ERROR STATE IS DIFFERENT, ON PURPOSE.
 * -----------------------------------------
 * A dead link is the one outcome the user has to be told about and cannot
 * resolve by waiting. It gets words and a way forward — still no card chrome,
 * but it is allowed to stop and say something.
 */

import { Loader2 } from "lucide-react";

interface AuthTransitionProps {
  /** "error" is the only state that renders anything but a spinner. */
  status: "pending" | "success" | "error";
  /** Shown only on error. */
  title?: string;
  message?: string;
  /** A way forward out of an error — rendered under the message. */
  action?: React.ReactNode;
  /** Announced to screen readers while the spinner is up. */
  busyLabel?: string;
}

export const AuthTransition = ({
  status,
  title,
  message,
  action,
  busyLabel = "Signing you in",
}: AuthTransitionProps) => (
  <div className="flex h-screen items-center justify-center bg-theme-bg-page px-4 font-inter">
    {status === "error" ? (
      <div className="flex max-w-sm flex-col items-center gap-4 text-center">
        <h1 className="text-lg font-bold text-theme-text-heading">{title}</h1>
        {message && <p className="text-14 leading-6 text-theme-text-muted">{message}</p>}
        {action}
      </div>
    ) : (
      /* `success` renders the same spinner as `pending`: the redirect fires on
         the next tick, so a separate success visual would flash for one frame
         and mean nothing. Keeping them identical is what stops this ever
         becoming a screen again.

         h-20 w-20 matches the route-level Suspense fallback in App.tsx — the
         spinner a hard refresh shows. Signing in is often the first thing
         someone sees in a session, and a second, smaller spinner for the same
         "the app is working" moment reads as a different kind of wait. Keep
         the two in step if either changes. */
      <Loader2 className="h-20 w-20 animate-spin text-primary-500" aria-label={busyLabel} />
    )}
  </div>
);
